import { StyleSheet, css } from 'aphrodite';
import type { FC } from 'react';

import { Button } from '../../../web/components-v2/Button';
import { Dropdown, DropdownContent, DropdownItem, DropdownSelectTrigger } from '../../../web/components-v2/Dropdown';
import { Toggle } from '../../../web/components/Toggle';

/**
 * Drawer-tab settings surface for the focused-view coaching pipeline.
 *
 * Mirrors the legacy Queue "Coaching Settings" panel
 * (`app/aida/components/Queue/index.tsx` lines 336-412): realtime coaching
 * toggle, auto-suggestions toggle (gated to realtime on), and the AI agent
 * template dropdown (gated to realtime on AND templates available). The
 * legacy panel's bottom "Use focused view" toggle is preserved here so a rep
 * can switch back to the standard queue (turning it off persists the opt-out
 * and navigates to `/queue`); a "Return to standard queue" link mirrors the
 * same action for discoverability.
 *
 * Pure presentation. Task 3.5 wires the parent (`FocusedLeadView`) to
 * `useCoachingPreferences` + `useAIAgentTemplateFilterInfiniteQuery`.
 */

type CoachingTemplate = {
	id: string;
	name: string;
};

export type SettingsPanelProps = {
	realtimeCoachingEnabled: boolean;
	autoSuggestionsEnabled: boolean;
	selectedCoachingTemplateId: string | null;
	templates: CoachingTemplate[];
	/** Whether the rep is currently opted into focused view (the "Use focused view" toggle state). */
	focusedViewEnabled: boolean;
	onRealtimeChange: (enabled: boolean) => void;
	onAutoSuggestionsChange: (enabled: boolean) => void;
	onTemplateChange: (id: string | null) => void;
	onReturnToQueue: () => void;
};

export const SettingsPanel: FC<SettingsPanelProps> = ({
	realtimeCoachingEnabled,
	autoSuggestionsEnabled,
	selectedCoachingTemplateId,
	templates,
	focusedViewEnabled,
	onRealtimeChange,
	onAutoSuggestionsChange,
	onTemplateChange,
	onReturnToQueue,
}) => {
	const selectedTemplateName = templates.find(t => t.id === selectedCoachingTemplateId)?.name || 'Default';
	const showAgentDropdown = realtimeCoachingEnabled && templates.length > 0;

	return (
		<div className={css(styles.root)}>
			<div className={css(styles.section)}>
				<h3 className={css(styles.title)}>Realtime Coaching</h3>
				<Toggle
					id='realtime-coaching-toggle'
					isOn={realtimeCoachingEnabled}
					text='Enable AI coaching during calls'
					onToggleCheckChanged={onRealtimeChange}
				/>
			</div>

			{realtimeCoachingEnabled && (
				<div className={css(styles.sectionDivider)}>
					<h3 className={css(styles.title)}>Auto Suggestions</h3>
					<Toggle
						id='auto-suggestions-toggle'
						isOn={autoSuggestionsEnabled}
						text='Automatically suggest during calls'
						onToggleCheckChanged={onAutoSuggestionsChange}
					/>
				</div>
			)}

			{showAgentDropdown && (
				<div className={css(styles.sectionDivider)}>
					<h3 className={css(styles.title)}>Choose AI Agent</h3>
					<Dropdown>
						<DropdownSelectTrigger styles={[styles.select]}>{selectedTemplateName}</DropdownSelectTrigger>
						<DropdownContent>
							<DropdownItem onClick={() => onTemplateChange(null)}>Default</DropdownItem>
							{templates.map(t => (
								<DropdownItem key={t.id} onClick={() => onTemplateChange(t.id)}>
									{t.name}
								</DropdownItem>
							))}
						</DropdownContent>
					</Dropdown>
				</div>
			)}

			<div className={css(styles.sectionDivider)}>
				<h3 className={css(styles.title)}>Focused View</h3>
				{/*
				 * Only rendered while focused view is on (always true for this panel,
				 * which lives inside the focused view). Gating the render avoids a dead
				 * "off" toggle with no way to turn focused view back on from here — the
				 * "Return to standard queue" link below is the unambiguous off action.
				 */}
				{focusedViewEnabled && (
					<Toggle
						id='focused-view-toggle'
						isOn={true}
						text='Use the new focused lead view'
						onToggleCheckChanged={next => {
							if (!next) onReturnToQueue();
						}}
					/>
				)}
				<Button variant='link' size='sm' styles={[styles.returnLink]} onClick={onReturnToQueue}>
					← Return to standard queue
				</Button>
			</div>
		</div>
	);
};

const styles = StyleSheet.create({
	root: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		// `--bg-elev` matches sibling drawer tabs (ConversationsPanel, PinConfigPanel)
		// so all tabs in the SideDrawer read as the same elevation surface.
		background: 'var(--bg-elev)',
		padding: 20,
		color: 'var(--ink)',
		overflowY: 'auto',
	},
	section: {
		paddingBottom: 4,
	},
	sectionDivider: {
		marginTop: 16,
		paddingTop: 16,
		borderTop: '1px solid var(--border)',
	},
	title: {
		fontSize: 14,
		fontWeight: 600,
		marginBottom: 10,
		marginTop: 0,
		color: 'var(--ink)',
	},
	select: {
		width: '100%',
	},
	returnLink: {
		padding: 0,
		marginTop: 12,
		justifyContent: 'flex-start',
	},
});
